import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import siteStructure from '../siteStructure.json';
import products from '../productDescription.json';
import services from '../serviceDescription.json';
import content from '../news/newsContentData.json';
import knowledgeData from '../knowledge/knowledgeBaseData.json';
import {useNews} from '../news/NewsContext';
import {useTheme} from './Header';
import {findSearchResults, normalizeSearch, searchSnippet} from './searchUtils';
import {useTranslation} from '../../i18n/translation';
import {buildLocalizedSearchIndex} from '../../i18n/search';
import '../../css/search-refresh.css';

const types = {all: 'Все', product: 'Продукция', service: 'Услуги', knowledge: 'База знаний', news: 'Новости'};
const labels = {product: 'Продукт', service: 'Услуга', knowledge: 'База знаний', news: 'Новость', page: 'Раздел сайта'};
function Highlight({text, query}) {
    const terms = normalizeSearch(query).trim().split(/[\s,]+/).filter(Boolean);
    const normalized = normalizeSearch(text);
    const parts = [];
    let start = 0;
    while (start < text.length) {
        let match = null;
        for (const term of terms) {
            const at = normalized.indexOf(term, start);
            if (at >= 0 && (!match || at < match.at || (at === match.at && term.length > match.length))) {
                match = {at, length: term.length};
            }
        }
        if (!match) { parts.push(text.slice(start)); break; }
        parts.push(text.slice(start, match.at), <mark key={match.at}>{text.slice(match.at, match.at + match.length)}</mark>);
        start = match.at + match.length;
    }
    return <>{parts}</>;
}
function SearchImage({src}) {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [src]);
    if (!src || failed) return null;
    return <img className="site-search-thumbnail" src={src} alt="" loading="lazy" onError={() => setFailed(true)}/>;
}
export function Search({searchState, setSearchState}) {
    const {newsData} = useNews();
    const {theme} = useTheme();
    const {english, t} = useTranslation();
    const [query, setQuery] = useState('');
    const [type, setType] = useState('all');
    const [limit, setLimit] = useState(8);
    const [displayed, setDisplayed] = useState({query: '', type: 'all'});
    const [leaving, setLeaving] = useState(false);
    useEffect(() => {
        if (query === displayed.query && type === displayed.type) {
            setLeaving(false);
            return;
        }
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        setLeaving(true);
        const timer = setTimeout(() => {
            setDisplayed({query, type});
            setLimit(8);
            setLeaving(false);
        }, reducedMotion ? 0 : 120);
        return () => clearTimeout(timer);
    }, [query, type, displayed]);
    const inputRef = useRef(null);
    const dialogRef = useRef(null);
    const index = useMemo(() => buildLocalizedSearchIndex(siteStructure, products, services, newsData, content, knowledgeData.articles, english), [newsData, english]);
    const results = useMemo(() => findSearchResults(index, displayed.query), [index, displayed.query]);
    const filtered = displayed.type === 'all' ? results : results.filter(item => item.type === displayed.type);
    useEffect(() => {
        if (!searchState) return;
        const previous = document.activeElement;
        inputRef.current?.focus();
        return () => previous?.focus?.();
    }, [searchState]);
    const handleKeys = event => {
        if (event.key === 'Escape') { event.stopPropagation(); setSearchState(false); }
        if (event.key === 'Tab') {
            const elements = dialogRef.current.querySelectorAll('button, input, a[href]');
            const first = elements[0], last = elements[elements.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
    };
    return <div className={`site-search ${theme} ${searchState ? 'is-open' : 'is-closed'}`} inert={searchState ? undefined : ''} aria-hidden={!searchState} role="dialog" aria-modal={searchState ? true : undefined} aria-labelledby="site-search-title" ref={dialogRef} onKeyDown={handleKeys}>
        <div className="site-search-panel">
            <header className="site-search-heading"><div><span>{t("СЕСПЕЛЬ / ПОИСК ПО САЙТУ")}</span><h2 id="site-search-title">{t("Что вы ищете?")}</h2></div><button type="button" onClick={() => setSearchState(false)} aria-label={t("Закрыть поиск")}>✕</button></header>
            <div className="site-search-field"><input ref={inputRef} aria-label={t("Поисковый запрос")} placeholder={t("Название, технология или характеристика")} value={query} onChange={event => {setQuery(event.target.value);}}/>{query && <button type="button" aria-label={t("Очистить запрос")} onClick={() => {setQuery(''); inputRef.current?.focus();}}>✕</button>}</div>
            <div className="site-search-types" role="group" aria-label={t("Тип результата")}>{Object.entries(types).map(([key, label]) => <button type="button" key={key} aria-pressed={type === key} onClick={() => {setType(key);}}>{t(label)}{query.trim() && <span>{key === 'all' ? results.length : results.filter(item => item.type === key).length}</span>}</button>)}</div>
            <p className="site-search-status" role="status">{query.trim() ? `${t("Найдено:")} ${filtered.length}` : t("Ищем по названиям, описаниям, характеристикам товаров, услуг, новостей и материалам базы знаний.")}</p>
            <div className={`site-search-output ${leaving ? 'is-updating' : ''}`} aria-busy={leaving} inert={leaving ? '' : undefined}>
            {displayed.query.trim() && !filtered.length && <div className="site-search-empty"><h3>{t("Ничего не найдено")}</h3><p>{t("Попробуйте сократить запрос или выбрать другой тип результата.")}</p></div>}
            <ul className="site-search-results" key={`${english}:${displayed.query}:${displayed.type}`}>{filtered.slice(0, limit).map((item, index) => <li key={item.url} style={{'--result-delay': `${(index % 8) * 65}ms`}}><Link to={item.url} onClick={() => setSearchState(false)}><SearchImage src={item.image}/><div className="site-search-result-text"><div className="site-search-meta"><span className="site-search-kind">{t(labels[item.type])}</span>{item.type === 'news' ? item.date && <><span className="site-search-separator" aria-hidden="true">·</span><time>{item.date}</time></> : item.type === 'knowledge' ? <><span className="site-search-separator" aria-hidden="true">·</span><span className="site-search-section">{item.category}</span></> : (item.type === 'product' || item.type === 'service') && item.sections?.map((section, index) => <React.Fragment key={index}><span className="site-search-separator" aria-hidden="true">›</span><span className="site-search-section">{section}</span></React.Fragment>)}</div><h3><Highlight text={item.title} query={displayed.query}/></h3><p><Highlight text={searchSnippet(item, displayed.query)} query={displayed.query}/></p></div></Link></li>)}</ul>
            {filtered.length > limit && <button type="button" className="site-search-more" onClick={() => setLimit(value => value + 8)}>{t("Показать ещё")} · {filtered.length - limit} {t("осталось")}</button>}
            </div>
        </div>
    </div>;
}

